import { createFileRoute } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, SectionHeader, Breadcrumbs, CTABand, FAQ } from "@/components/site/Blocks";
import { buildHead } from "@/lib/seo-head";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import { FhaRateTool } from "@/components/fha/FhaRateTool";
import { FHA_STATE_LIST_LABEL } from "@/lib/fha/eligibility";

const URL = "/fha-loan-rates";
const KEYWORDS = [
  "FHA loan rates",
  "FHA mortgage rates today",
  "FHA mortgage pricing tool",
  "FHA 30 year fixed rate",
  "FHA refinance rates",
  "FHA mortgage insurance premium",
  "FHA loan calculator with MIP",
  "FHA first time home buyer rates",
  "FHA cash-out refinance rates",
  "FHA loan rates Florida",
  "FHA loan rates Colorado",
  "FHA 3.5% down payment loan",
].join(", ");

const FAQS = [
  {
    q: "Is the pricing on this page live?",
    a: "Yes. Each option reflects live wholesale pricing for the exact scenario you enter. Rates and fees are based on the selected loan scenario, may change before closing, and are not an approval, a commitment to lend, or an offer of credit.",
  },
  {
    q: "Why is the APR different from the interest rate?",
    a: "The interest rate sets your principal and interest payment. The APR is a broader cost measure that reflects certain financing charges over the loan term. APR is shown for the selected loan scenario.",
  },
  {
    q: "What is FHA mortgage insurance?",
    a: "FHA-insured loans carry an upfront mortgage insurance premium, which is commonly financed into the loan amount, plus an annual premium collected monthly as part of your payment. The monthly premium is shown on the quote card and in the full breakdown where it is available for your scenario.",
  },
  {
    q: "Does the payment shown include taxes and insurance?",
    a: "No. The figures shown cover principal and interest, and the FHA monthly mortgage insurance premium where returned. Property taxes, homeowner's insurance, HOA dues, and other escrowed items are additional.",
  },
  {
    q: "How much down payment does FHA require?",
    a: "FHA financing generally allows a low down payment, commonly 3.5% of the purchase price for qualifying borrowers. The exact requirement depends on credit profile, the property, and lender and program requirements confirmed in underwriting.",
  },
  {
    q: "Which states can I get FHA pricing for here?",
    a: `FHA is an owner-occupied consumer mortgage, so we show it only where we hold a consumer mortgage brokerage license: ${FHA_STATE_LIST_LABEL}. The broader state footprint elsewhere on this site is business-purpose investor financing and does not extend to FHA.`,
  },
  {
    q: "Are FHA loan limits applied to my scenario?",
    a: "County FHA loan limits apply and are validated in underwriting against the property's location. If your requested amount exceeds the applicable limit, a loan officer will review alternatives with you.",
  },
  {
    q: "Can I use FHA to refinance?",
    a: "Yes. FHA supports rate-and-term and cash-out refinances, each with its own eligibility and equity requirements. Select the purpose in the tool to price that scenario.",
  },
  {
    q: "Does pricing this scenario affect my credit?",
    a: "This tool prices a scenario you type in. It does not submit an application. Any credit review happens later, with your authorization, as part of a completed application.",
  },
  {
    q: "What credit score do I need?",
    a: "FHA programs accommodate a range of credit profiles, and pricing improves as credit strengthens. Enter your score to see what the engine returns; final eligibility is determined in full underwriting.",
  },
  {
    q: "Can I buy a 2-4 unit property with FHA?",
    a: "FHA permits 2-4 unit properties when you occupy one of the units as your primary residence. Select the unit count in the tool to price that scenario.",
  },
  {
    q: "Is Simply Approved Mortgages part of FHA or HUD?",
    a: "No. We are an independent mortgage broker, NMLS #2620881. We are not a government agency and are not acting for, affiliated with, or endorsed by FHA, HUD, or any other government agency.",
  },
];

export const Route = createFileRoute("/fha-loan-rates")({
  head: () =>
    buildHead({
      title: "FHA Loan Rates — Live FHA Mortgage Pricing Tool",
      description:
        "Price a real FHA purchase or refinance scenario: interest rate, APR, points or lender credit, principal and interest, and monthly FHA mortgage insurance where returned.",
      url: URL,
      keywords: KEYWORDS,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "FHA Loan Rates", url: URL },
      ],
      service: { name: "FHA Mortgage Pricing" },
      faqs: FAQS,
    }),
  component: FhaRatesPage,
});

function FhaRatesPage() {
  return (
    <SiteShell hideBreadcrumbs productLabel="FHA">
      <PageHero
        eyebrow="FHA Mortgage Rates"
        title="FHA Loan Rates & Pricing Tool"
        subtitle="Enter your purchase or refinance scenario and see what wholesale lenders are actually pricing today — interest rate, APR, points or lender credit, principal and interest, and FHA monthly mortgage insurance. Nothing on this page is a sample figure."
        primaryHref="/apply?product=fha"
      />
      <PageEssentials />
      <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "FHA Loan Rates" }]} />

      <Section id="pricing">
        <SectionHeader
          eyebrow="Live pricing"
          title="Price your FHA scenario"
          intro="Set the purpose, location, price, down payment, credit score and term. Every figure shown reflects live pricing for that exact scenario."
        />
        <div className="mt-8">
          <FhaRateTool id="fha-pricing" />
        </div>
      </Section>

      <Section id="how-fha-pricing-works">
        <SectionHeader
          eyebrow="How to read it"
          title="Rate, APR, points and mortgage insurance"
          intro="Four numbers drive an FHA quote, and they move together."
        />
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-5">
            <h3 className="font-display text-base font-semibold text-foreground">Rate vs APR</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              The interest rate determines your principal and interest payment. The APR is a broader cost
              measure across the loan term. APR is shown for the selected loan
              scenario.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <h3 className="font-display text-base font-semibold text-foreground">
              Points and lender credit
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              A lower rate usually costs discount points at closing; a higher rate can return a lender
              credit toward closing costs. Par means neither. The cards show the lowest rate, the option
              closest to par, and the largest lender credit so the trade-off is visible.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <h3 className="font-display text-base font-semibold text-foreground">
              Upfront and annual FHA insurance
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              FHA loans carry an upfront mortgage insurance premium, commonly financed into the loan
              amount, and an annual premium collected monthly. That is why the total loan amount can
              exceed your base loan amount in the breakdown.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <h3 className="font-display text-base font-semibold text-foreground">What is not included</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Property taxes, homeowner's insurance, HOA dues and other escrowed items are not in the
              payment figures shown. Ask a licensed loan officer for a full written cost estimate for your
              property.
            </p>
          </div>
        </div>
      </Section>

      <Section id="faq">
        <SectionHeader eyebrow="FAQ" title="FHA loan rate questions" />
        <div className="mt-8">
          <FAQ items={FAQS} />
        </div>
      </Section>

      <CTABand
        title="Have a licensed loan officer review your FHA scenario"
        body="Share the property, price, down payment and credit profile and get written pricing back — no obligation and no guaranteed approval."
        href="/apply?product=fha"
      />
      <PageOutro />
    </SiteShell>
  );
}
